import { useCallback, useEffect } from 'react';
import useContactStore from '@/stores/ContactStore';
import useInboxStore from '@/stores/InboxStore';
import { ArrowLeft, XIcon } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { trackProfileCloseButtonClicked } from '@/lib/analytics-event';
import { cn } from '@/lib/utils';
import { fetchContactDetails, fetchContactMessages } from '@/hooks/contact';

import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import ContactHead from './chat/ContactHead';
import ContactMessages from './chat/ContactMessages';
import ContactDetails from './contact-card/ContactDetail';

const ContactChat = () => {
  const { id } = useParams();
  const { contactSidebarShow, setContactSidebarShow } = useInboxStore(
    useShallow((state) => ({
      contactSidebarShow: state.contactSidebarShow,
      setContactSidebarShow: state.setContactSidebarShow,
    }))
  );

  const {
    setContactDetails,
    setMessages,
    changeContact,
    setPage,
    resetCommentThread,
    resetSelectedPrivateReplyMessage,
    isShowingMobileProfile,
    setIsShowingMobileProfile,
  } = useContactStore(
    useShallow((state) => ({
      setContactDetails: state.setContactDetails,
      setMessages: state.setMessages,
      changeContact: state.changeContact,
      setPage: state.setPage,
      resetCommentThread: state.resetCommentThread,
      resetSelectedPrivateReplyMessage: state.resetSelectedPrivateReplyMessage,
      isShowingMobileProfile: state.isShowingMobileProfile,
      setIsShowingMobileProfile: state.setIsShowingMobileProfile,
    }))
  );

  const handleNewResponse = useCallback(
    (messages, page) => {
      setMessages(messages);
      setPage(page);
    },
    [setMessages, setPage]
  );

  const fetchContactData = useCallback(async () => {
    try {
      const response = await fetchContactDetails(id);
      setContactDetails(response);
    } catch (error) {
      toast.error('Unable to fetch contact information');
    }
  }, [id, setContactDetails]);

  const fetchContactMessageData = useCallback(async () => {
    try {
      const contactMessageResponse = await fetchContactMessages(id, 1);
      const { data, next_page_url, current_page, response_type } =
        contactMessageResponse;

      // Store data in messages array with response_type flag
      // This allows pagination to work the same way for both email threads and regular messages
      handleNewResponse(
        data,
        next_page_url ? current_page + 1 : null,
        response_type
      );
    } catch (error) {
      toast.error('Unable to fetch contact messages.');
    }
  }, [id, handleNewResponse]);

  const closeContactSidebar = () => {
    trackProfileCloseButtonClicked();
    setContactSidebarShow();
  };

  useEffect(() => {
    setIsShowingMobileProfile(false);
    changeContact(id);
    fetchContactMessageData();
    fetchContactData();
    resetCommentThread();
    resetSelectedPrivateReplyMessage();
  }, [
    id,
    fetchContactData,
    fetchContactMessageData,
    changeContact,
    resetCommentThread,
    resetSelectedPrivateReplyMessage,
    setIsShowingMobileProfile,
  ]);

  return (
    <div className='flex'>
      <div
        className={cn(
          'flex max-h-screen min-h-screen flex-grow flex-col',
          isShowingMobileProfile ? 'hidden md:flex' : 'flex'
        )}
      >
        <ContactHead
          onToggleProfile={setContactSidebarShow}
          onShowMobileProfile={() => setIsShowingMobileProfile(true)}
        />
        <ContactMessages />
      </div>
      {isShowingMobileProfile && (
        <div
          className={cn(
            'absolute inset-0 z-10 flex h-full w-full flex-col bg-background md:hidden'
          )}
        >
          <div className='flex h-14 flex-shrink-0 items-center border-b px-4'>
            <Button
              variant='ghost'
              size='icon'
              onClick={() => setIsShowingMobileProfile(false)}
              className='mr-2'
            >
              <ArrowLeft className='h-5 w-5' />
              <span className='sr-only'>Back to Chat</span>
            </Button>
            <h2 className='text-lg font-semibold'>Contact Profile</h2>
          </div>
          <div className='flex-1 overflow-y-auto'>
            <ContactDetails />
          </div>
        </div>
      )}
      {!isShowingMobileProfile && contactSidebarShow && (
        <div
          className={cn(
            'justify-center',
            'hidden md:flex',
            'md:min-w-[420px] md:max-w-[420px]'
          )}
        >
          <Separator orientation={'vertical'} />
          <div className=' flex h-screen w-full flex-col '>
            <ScrollArea className='h-full'>
              <div className='absolute right-0 top-0 z-20 mx-2 my-1 flex items-center justify-end'>
                <Button variant='ghost' size='xs' onClick={closeContactSidebar}>
                  <XIcon className='h-5 w-5' />
                </Button>
              </div>
              <ContactDetails />
            </ScrollArea>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactChat;
